import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  InputAdornment,
  Link,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import VisibilityRounded from '@mui/icons-material/VisibilityRounded';
import VisibilityOffRounded from '@mui/icons-material/VisibilityOffRounded';
import { StepperPageShell } from 'components/common/stepperPage';
import DischargeInstructionCards from 'components/dashboard/discharge/DischargeInstructionCards';
import { db } from 'services/firebase';
import {
  ensureAnonymousFirebaseUser,
  getRestoredFirebaseUser,
} from 'services/auth/ensureAnonymousAuth';
import {
  claimDischargeHandoff,
  transferGuestDischargeInstructions,
} from 'services/healthRecordsShareService';
import { upgradeDischargeGuestToAccount } from 'features/dischargeHandoff/dischargeGuestUpgrade';
import DischargePdfButton from 'components/dashboard/discharge/DischargePdfButton';

const HANDOFF_ID_PATTERN = /^[0-9a-f]{48}$/;
// Firebase's own floor, checked before the round trip.
const MIN_PASSWORD_LENGTH = 6;

// The page's own errors are kept as their keys (medications namespace) and translated at
// render; anything else in `error` is a message from elsewhere, shown as it came.
const USED_OR_EXPIRED = 'dischargeHandoff.usedOrExpired';
const GENERIC_ERROR = 'dischargeHandoff.genericError';
const INCOMPLETE_LINK = 'dischargeHandoff.incompleteLink';
const PAGE_ERRORS = [USED_OR_EXPIRED, GENERIC_ERROR, INCOMPLETE_LINK];

/**
 * What lets a guest who signs in to an EXISTING account take these instructions
 * with them: the guest uid plus the one-time secret the claim returned, kept in
 * this browser only. No PHI. Cleared once the move succeeds.
 * See transferGuestDischargeInstructions.
 */
const transferKey = (handoffId) => `dischargeGuestTransfer:${handoffId}`;

const readTransfer = (handoffId) => {
  try {
    return JSON.parse(localStorage.getItem(transferKey(handoffId)) || 'null');
  } catch {
    return null;
  }
};

const writeTransfer = (handoffId, value) => {
  try {
    if (value) localStorage.setItem(transferKey(handoffId), JSON.stringify(value));
    else localStorage.removeItem(transferKey(handoffId));
  } catch {
    /* private browsing: the move after sign-in just is not offered */
  }
};

/**
 * Public page a patient's phone opens by scanning the provider's discharge QR
 * (route: /discharge?h=<handoffId>).
 *
 * Works with no account, like CareStart: the phone signs in anonymously if nobody
 * is signed in, claims the handoff into that account, and shows the instructions
 * straight away. A patient who is already signed in gets them saved to their real
 * account instead. See functions/src/accounts/dischargeHandoff.js for why the claim
 * is one-time and why a guest claim still needs a uid.
 *
 * ⚠️ READ FIRST, CLAIM SECOND. The code is single-use, so a reload after the claim
 * would be refused. The instructions are already in this phone's account by then,
 * so the page looks there before it asks the server for anything. That is also why
 * the link keeps working on this phone after the code's hour is up.
 */
const DischargeHandoffPage = () => {
  const [searchParams] = useSearchParams();
  const handoffId = (searchParams.get('h') || '').trim().toLowerCase();
  // Set by Verify.js: back from the verification email, so the account already exists.
  const fromVerification = searchParams.get('verified') === '1';
  const navigate = useNavigate();
  const { t } = useTranslation('medications');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [delivery, setDelivery] = useState(null);
  const [isGuest, setIsGuest] = useState(false);
  // Lifted from SaveInstructionsOffer so the banner at the top can open the form at the
  // bottom. Once it is open the banner has done its job and goes.
  const [offerFormOpen, setOfferFormOpen] = useState(false);
  const offerRef = useRef(null);
  const openOfferFromTop = useCallback(() => {
    setOfferFormOpen(true);
    // After the form has rendered, or the scroll lands on the old, shorter offer.
    requestAnimationFrame(() =>
      offerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }),
    );
  }, []);
  const [attempt, setAttempt] = useState(0);

  // Arriving here consumes the post-sign-in return set by "Sign in" below, so a
  // later visit to the dashboard does not bounce back.
  useEffect(() => {
    try {
      if ((localStorage.getItem('pendingShareReturn') || '').startsWith('/discharge')) {
        localStorage.removeItem('pendingShareReturn');
      }
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    if (!HANDOFF_ID_PATTERN.test(handoffId)) {
      setError(INCOMPLETE_LINK);
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        // Back from the verification email: the instructions belong to the account just
        // created, so this must never become a guest (who would find nothing here and a
        // spent code). No session in this browser means sign in, then come back.
        if (fromVerification) {
          const restored = await getRestoredFirebaseUser();
          if (cancelled) return;
          if (!restored || restored.isAnonymous) {
            try {
              localStorage.setItem('pendingShareReturn', `/discharge?h=${handoffId}`);
            } catch {
              /* private browsing: they land on the dashboard after sign-in instead */
            }
            navigate('/auth?role=patient', { replace: true });
            return;
          }
        }
        const user = await ensureAnonymousFirebaseUser({ logPrefix: '[DischargeHandoff]' });
        if (!user) throw new Error(GENERIC_ERROR);
        const ref = db
          .collection('users')
          .doc(user.uid)
          .collection('dischargeInstructions')
          .doc(handoffId);

        let snap = await ref.get();
        if (!snap.exists) {
          const pendingTransfer = readTransfer(handoffId);
          if (!user.isAnonymous && pendingTransfer && pendingTransfer.guestUid !== user.uid) {
            // Scanned as a guest, then signed in to an existing account.
            await transferGuestDischargeInstructions({ handoffId, ...pendingTransfer });
            writeTransfer(handoffId, null);
          } else {
            const result = await claimDischargeHandoff(handoffId);
            if (result?.transferSecret) {
              writeTransfer(handoffId, {
                guestUid: user.uid,
                transferSecret: result.transferSecret,
              });
            }
          }
          snap = await ref.get();
        }
        if (cancelled) return;
        if (!snap.exists) throw new Error(GENERIC_ERROR);
        setDelivery(snap.data());
        setIsGuest(Boolean(user.isAnonymous));
      } catch (err) {
        if (cancelled) return;
        console.error('[DischargeHandoffPage] open failed', err);
        const refused =
          err?.code === 'functions/failed-precondition' || err?.code === 'failed-precondition';
        setError(refused ? USED_OR_EXPIRED : err?.message || GENERIC_ERROR);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [handoffId, attempt, fromVerification, navigate]);

  const fromLabel = delivery
    ? [delivery.providerName, delivery.organizationName].filter(Boolean).join(', ')
    : '';

  return (
    <StepperPageShell standalone maxWidth={700}>
      {/* Extra side padding on top of the shell's: this is a reading page on a phone,
          and the cards' accent borders sat right against the screen edge. */}
      <Box sx={{ px: { xs: 1, sm: 0 }, pt: { xs: 2, sm: 4 } }}>
        <Stack spacing={1} alignItems="center" textAlign="center" sx={{ mb: 4 }}>
          <Typography variant="h5" component="h1" sx={{ fontWeight: 600 }}>
            {t('discharge.title')}
          </Typography>
          <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
            {t('dischargeHandoff.subtitle')}
          </Typography>
          {fromLabel && (
            <Typography variant="body2" color="text.secondary">
              {t('dischargeHandoff.from', { name: fromLabel })}
            </Typography>
          )}
        </Stack>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Stack spacing={2} alignItems="center">
            <Alert severity="error" sx={{ width: '100%' }}>
              {PAGE_ERRORS.includes(error) ? t(error) : error}
            </Alert>
            {error !== USED_OR_EXPIRED && HANDOFF_ID_PATTERN.test(handoffId) && (
              <Button onClick={() => setAttempt((n) => n + 1)}>
                {t('dischargeHandoff.tryAgain')}
              </Button>
            )}
          </Stack>
        ) : (
          <>
            {!isGuest && (
              <Alert
                severity="success"
                sx={{ mb: 3 }}
                action={
                  <Button
                    color="inherit"
                    size="small"
                    component={RouterLink}
                    to="/dashboard/discharge-instructions"
                  >
                    {t('dischargeHandoff.open')}
                  </Button>
                }
              >
                {t('dischargeHandoff.saved')}
              </Alert>
            )}

            {/* The client wanted the account offer at the top. One line and a button, so the
                instructions (what the patient came for) stay above the fold on a phone; the
                full pitch, the form and Sign in stay at the bottom, where this scrolls to. */}
            {isGuest && !offerFormOpen && (
              <Alert
                severity="info"
                sx={{ mb: 3 }}
                action={
                  <Button color="inherit" size="small" onClick={openOfferFromTop}>
                    {t('dischargeHandoff.createAccount')}
                  </Button>
                }
              >
                {t('dischargeHandoff.saveBanner')}
              </Alert>
            )}

            <DischargePdfButton instructions={delivery?.instructions} />

            <DischargeInstructionCards instructions={delivery?.instructions} />

            {isGuest && (
              <Box ref={offerRef} sx={{ scrollMarginTop: 16 }}>
                <SaveInstructionsOffer
                  handoffId={handoffId}
                  formOpen={offerFormOpen}
                  onOpenForm={() => setOfferFormOpen(true)}
                />
              </Box>
            )}
          </>
        )}
      </Box>
    </StepperPageShell>
  );
};

/**
 * The upsell: one sentence under the instructions, which are what the patient came
 * for and are already on this phone either way. One filled button, so the next step
 * is obvious without the page turning into a pitch. The form only appears on request.
 */
const SaveInstructionsOffer = ({ handoffId, formOpen, onOpenForm }) => {
  const navigate = useNavigate();
  const { t } = useTranslation('medications');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [done, setDone] = useState(null);

  const { control, handleSubmit } = useForm({ defaultValues: { email: '', password: '' } });

  const onSubmit = useCallback((values) => {
    setSubmitting(true);
    setError('');
    upgradeDischargeGuestToAccount({ ...values, handoffId })
      .then(setDone)
      .catch((err) => setError(err?.message || t('dischargeHandoff.createFailed')))
      .finally(() => setSubmitting(false));
  }, [handoffId, t]);

  // Sign-in replaces this guest session. DashboardPage sends them back here after,
  // and the page then moves the instructions across (see readTransfer).
  const handleSignIn = useCallback(
    (event) => {
      event.preventDefault();
      try {
        localStorage.setItem('pendingShareReturn', `/discharge?h=${handoffId}`);
      } catch {
        /* ignore */
      }
      navigate('/auth?role=patient');
    },
    [handoffId, navigate],
  );

  if (done) {
    return (
      <Alert severity="success" sx={{ mt: 3 }}>
        {t('dischargeHandoff.accountReady')}{' '}
        {done.verificationSent
          ? t('dischargeHandoff.confirmEmail')
          : t('dischargeHandoff.findUnder')}
      </Alert>
    );
  }

  return (
    <Stack spacing={2} sx={{ mt: 3 }}>
      <Divider />
      {!formOpen && (
        <Stack spacing={1.5} alignItems="center" textAlign="center">
          <Typography variant="body2" color="text.secondary">
            {/* Benefits the app actually delivers: every visit's instructions, a record the
                patient can share by QR, and VisitStart pre-filling the next check-in. No
                30-day line: the PDF covers "can I get back to this", and saying it only
                weakens the reason to sign up. */}
            {t('dischargeHandoff.pitch')}
          </Typography>
          <Button variant="contained" onClick={onOpenForm}>
            {t('dischargeHandoff.createFreeAccount')}
          </Button>
        </Stack>
      )}

      {formOpen && (
        <Stack component="form" spacing={2} noValidate onSubmit={handleSubmit(onSubmit)}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {t('dischargeHandoff.saveTitle')}
          </Typography>
          {error && (
            <Alert severity="error" onClose={() => setError('')}>
              {error}
            </Alert>
          )}
          <Controller
            name="email"
            control={control}
            rules={{
              required: t('dischargeHandoff.emailRequired'),
              pattern: {
                value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                message: t('dischargeHandoff.emailInvalid'),
              },
            }}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                type="email"
                label={t('dischargeHandoff.email')}
                fullWidth
                autoComplete="email"
                autoFocus
                error={Boolean(fieldState.error)}
                helperText={fieldState.error?.message}
              />
            )}
          />
          <Controller
            name="password"
            control={control}
            rules={{
              required: t('dischargeHandoff.passwordRequired'),
              minLength: {
                value: MIN_PASSWORD_LENGTH,
                message: t('dischargeHandoff.passwordTooShort', { count: MIN_PASSWORD_LENGTH }),
              },
            }}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                type={showPassword ? 'text' : 'password'}
                label={t('dischargeHandoff.password')}
                fullWidth
                autoComplete="new-password"
                error={Boolean(fieldState.error)}
                helperText={fieldState.error?.message}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword((prev) => !prev)}
                        edge="end"
                        aria-label={
                          showPassword
                            ? t('dischargeHandoff.hidePassword')
                            : t('dischargeHandoff.showPassword')
                        }
                      >
                        {showPassword ? <VisibilityOffRounded /> : <VisibilityRounded />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            )}
          />
          <LoadingButton type="submit" variant="contained" loading={submitting}>
            {t('dischargeHandoff.createAccount')}
          </LoadingButton>
        </Stack>
      )}

      {/* Visible with the form open too: "this email already has an account" points here. */}
      <Typography variant="body2" color="text.secondary" textAlign="center">
        {t('dischargeHandoff.alreadyHaveAccount')}{' '}
        <Link href="/auth?role=patient" variant="body2" onClick={handleSignIn}>
          {t('dischargeHandoff.signIn')}
        </Link>
      </Typography>
    </Stack>
  );
};

export default DischargeHandoffPage;
